import { trafficSource } from '@/lib/link-in-bio/model';

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type BrowserBioContext = Record<UtmKey, string> & { referrer: string };

/** Bounded labels only: URLs, email addresses and control characters are not analytics labels. */
export function analyticsLabel(value: unknown, limit = 120): string {
  if (typeof value !== 'string') return '';
  const clean = value
    .normalize('NFKC')
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .trim();
  if (clean.includes('@') || /https?:\/\//i.test(clean)) return '';
  return clean.slice(0, limit);
}

export function referrerHost(value: unknown): string {
  if (typeof value !== 'string' || !value || value.length > 2048) return '';
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    // Exclude local/IP referrers and never retain paths, queries or credentials.
    if (!host.includes('.') || /^[\d.]+$/.test(host) || host.includes(':')) return '';
    return host.length <= 253 ? host : '';
  } catch {
    return '';
  }
}

export function browserBioContext(referrer: string, search: string): BrowserBioContext {
  const params = new URLSearchParams(search);
  return {
    referrer: referrerHost(referrer),
    utm_source: analyticsLabel(params.get('utm_source')),
    utm_medium: analyticsLabel(params.get('utm_medium')),
    utm_campaign: analyticsLabel(params.get('utm_campaign')),
    utm_term: analyticsLabel(params.get('utm_term')),
    utm_content: analyticsLabel(params.get('utm_content')),
  };
}

export function serverBioContext(input: BrowserBioContext, headers: Headers, vercel: boolean) {
  const referrer = referrerHost(input.referrer);
  const utm_source = analyticsLabel(input.utm_source);
  const country = vercel ? (headers.get('x-vercel-ip-country') ?? '').toUpperCase() : '';
  const region = vercel ? (headers.get('x-vercel-ip-country-region') ?? '').toUpperCase() : '';
  let city = '';
  if (vercel) {
    try {
      city = analyticsLabel(decodeURIComponent(headers.get('x-vercel-ip-city') ?? ''), 80);
    } catch {
      /* Malformed upstream location stays unknown. */
    }
  }
  const ua = headers.get('user-agent') ?? '';
  return {
    referrer,
    utm_source,
    utm_medium: analyticsLabel(input.utm_medium),
    utm_campaign: analyticsLabel(input.utm_campaign),
    utm_term: analyticsLabel(input.utm_term),
    utm_content: analyticsLabel(input.utm_content),
    source: trafficSource(referrer ? `https://${referrer}` : ''),
    device: !ua ? 'Other' : /Mobi|Android|iPhone|iPad/i.test(ua) ? 'Mobile' : 'Desktop',
    country: /^[A-Z]{2}$/.test(country) ? country : '',
    region: /^[A-Z0-9]{1,3}$/.test(region) ? region : '',
    city,
  };
}
